import React, { useState, useEffect } from 'react';
import { Play, Settings, Music, Clock } from 'lucide-react';
import { pitchTrainingService } from '../../../services/pitchTrainingService';

export default function PracticeSetup({
  selectedTrack,
  setSelectedTrack,
  mode,
  setMode,
  settings,
  setSettings,
  onStartPractice,
  recentSessions
}) {
  const [tracks, setTracks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showSettings, setShowSettings] = useState(false);

  useEffect(() => {
    loadTracks();
  }, []);

  const loadTracks = async () => {
    try {
      const { data } = await pitchTrainingService?.getAllTracks();
      setTracks(data || []);
    } catch (err) {
      console.error('Error loading tracks:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Track Selection */}
      <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20">
        <h2 className="text-xl font-bold text-white mb-4">Select a Song</h2>
        {loading ? (
          <p className="text-white/70">Loading tracks...</p>
        ) : tracks?.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {tracks?.map((track) => (
              <div
                key={track?.id}
                onClick={() => setSelectedTrack(track)}
                className={`p-4 rounded-xl cursor-pointer transition-all ${
                  selectedTrack?.id === track?.id
                    ? 'bg-purple-600/30 border-2 border-purple-400' :'bg-white/5 border-2 border-transparent hover:bg-white/10'
                }`}
              >
                <div className="flex items-center gap-3">
                  <Music className="w-8 h-8 text-purple-400" />
                  <div className="flex-1">
                    <p className="text-white font-medium">{track?.title}</p>
                    <p className="text-white/70 text-sm">{track?.musicalTitle}</p>
                    <p className="text-white/50 text-xs mt-1">{track?.noteCount} notes</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-white/70">No tracks available</p>
        )}
      </div>
      {/* Mode Selection */}
      <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20">
        <h2 className="text-xl font-bold text-white mb-4">Voice Mode</h2>
        <div className="grid grid-cols-3 gap-4">
          {['mix', 'legit', 'belt']?.map((m) => (
            <button
              key={m}
              onClick={() => setMode(m)}
              className={`py-3 px-4 rounded-xl font-medium transition-all ${
                mode === m
                  ? 'bg-purple-600 text-white' :'bg-white/5 text-white/70 hover:bg-white/10'
              }`}
            >
              {m?.charAt(0)?.toUpperCase() + m?.slice(1)}
            </button>
          ))}
        </div>
      </div>
      {/* Settings */}
      <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20">
        <button
          onClick={() => setShowSettings(!showSettings)}
          className="w-full flex items-center justify-between text-white font-bold mb-4"
        >
          <span className="flex items-center gap-2">
            <Settings className="w-5 h-5" />
            Advanced Settings
          </span>
          <span className="text-2xl">{showSettings ? '−' : '+'}</span>
        </button>

        {showSettings && (
          <div className="space-y-4 pt-4 border-t border-white/20">
            <div>
              <label className="block text-white/80 text-sm mb-2">
                Pitch Tolerance (cents): {settings?.pitchTolerance}
              </label>
              <input
                type="range"
                min="10"
                max="50"
                value={settings?.pitchTolerance}
                onChange={(e) => setSettings({ ...settings, pitchTolerance: parseInt(e?.target?.value) })}
                className="w-full"
              />
            </div>

            <div>
              <label className="block text-white/80 text-sm mb-2">
                Timing Window (ms): {settings?.timingWindow}
              </label>
              <input
                type="range"
                min="50"
                max="200"
                value={settings?.timingWindow}
                onChange={(e) => setSettings({ ...settings, timingWindow: parseInt(e?.target?.value) })}
                className="w-full"
              />
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={settings?.playReference}
                onChange={(e) => setSettings({ ...settings, playReference: e?.target?.checked })}
                className="w-4 h-4"
              />
              <label className="text-white/80 text-sm">Play reference melody</label>
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={settings?.metronome}
                onChange={(e) => setSettings({ ...settings, metronome: e?.target?.checked })}
                className="w-4 h-4"
              />
              <label className="text-white/80 text-sm">Metronome click</label>
            </div>
          </div>
        )}
      </div>
      {/* Recent Sessions */}
      {recentSessions?.length > 0 && (
        <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20">
          <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
            <Clock className="w-6 h-6 text-blue-400" />
            Recent Practice Sessions
          </h2>
          <div className="space-y-2">
            {recentSessions?.map((session) => (
              <div key={session?.id} className="p-3 bg-white/5 rounded-lg">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-white font-medium">{session?.trackTitle}</p>
                    <p className="text-white/70 text-sm">{session?.musicalTitle}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-white font-bold text-lg">{session?.grade}</p>
                    <p className="text-white/70 text-sm">{session?.pitchAccuracyPercent}%</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {/* Start Button */}
      <button
        onClick={onStartPractice}
        disabled={!selectedTrack}
        className="w-full py-4 bg-purple-600 hover:bg-purple-700 text-white rounded-xl font-bold text-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
      >
        <Play className="w-6 h-6" />
        Start Practice
      </button>
    </div>
  );
}